import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { StatusIcon } from "@/components/StatusIcon";
import NZTerritoryMap from "@/components/NZTerritoryMap";
import {
  Flower2, Truck, User, Receipt, Settings, LogOut, ChevronRight,
  Pause, Play, X, AlertCircle, Check, Loader2, CreditCard, Sprout,
} from "lucide-react";

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: Flower2, iconKey: "account.subscriptions" },
  { id: "subscription", label: "My Subscription", icon: Sprout, iconKey: "account.subscriptions" },
  { id: "deliveries", label: "My Deliveries", icon: Truck, iconKey: "delivery.scheduled" },
  { id: "grower", label: "My Grower", icon: User, iconKey: "account.profile" },
  { id: "billing", label: "Billing & Payments", icon: Receipt, iconKey: "account.payment" },
  { id: "account", label: "My Account", icon: Settings, iconKey: "account.preferences" },
];

// Pre-computed sticker grid (Dashboard excluded). Static, safe to compute once.
const STICKER_SECTIONS = SECTIONS.filter((s) => s.id !== "dashboard");

export default function SubscriberPage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/dashboard");
      setDashboard(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // Show welcome toast if user just came back from checkout
    if (location.state?.justSubscribed) setShowWelcome(true);
  }, []);

  useEffect(() => {
    if (showWelcome) {
      const t = setTimeout(() => setShowWelcome(false), 5000);
      return () => clearTimeout(t);
    }
  }, [showWelcome]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="club-theme min-h-screen flex flex-col" data-testid="subscriber-portal">
      <PublicHeader />
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-64 flex-shrink-0 border-r border-[var(--bloom-border)] bg-[var(--bloom-bg)] hidden md:flex flex-col sticky top-0 self-start h-[calc(100vh)]" data-testid="subscriber-sidebar">
          <div className="p-6 border-b border-[var(--bloom-border)]">
            <div className="font-serif text-base leading-tight uppercase tracking-[0.04em]">My Bloom Club</div>
            <div className="font-serif italic text-sm leading-tight text-[var(--bloom-ink-soft)] mt-0.5">{user?.name}</div>
          </div>
          <nav className="flex-1 p-3 overflow-y-auto" data-testid="subscriber-nav">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === active;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 mb-1 transition ${
                    isActive
                      ? "bg-[var(--bloom-ink)] text-[#FAF4FA]"
                      : "text-[var(--bloom-ink-soft)] hover:bg-[var(--bloom-surface)] hover:text-[var(--bloom-ink)]"
                  }`}
                  style={{ borderRadius: 6 }}
                  data-testid={`subscriber-tab-${s.id}`}
                >
                  <span className="flex items-center gap-3">
                    <StatusIcon
                      name={s.iconKey}
                      size={20}
                      fallback={<Icon size={16} strokeWidth={1.5} />}
                    />
                    <span className="font-serif text-base">{s.label}</span>
                  </span>
                  {isActive && <ChevronRight size={14} />}
                </button>
              );
            })}
          </nav>
          <div className="p-4 border-t border-[var(--bloom-border)]">
            <button onClick={onLogout} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink)]" data-testid="subscriber-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        {/* Mobile tab strip */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[var(--bloom-bg)] border-t border-[var(--bloom-border)] z-30">
          <div className="flex overflow-x-auto">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === active;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`flex-1 min-w-[80px] flex flex-col items-center gap-1 py-2.5 px-2 transition ${
                    isActive ? "text-[var(--bloom-ink)]" : "text-[var(--bloom-ink-muted)]"
                  }`}
                >
                  <Icon size={18} strokeWidth={1.5} />
                  <span className="text-[9px] tracking-[0.12em] uppercase">{s.label.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main */}
        <main className="flex-1 min-w-0 px-6 md:px-12 py-10 pb-28 md:pb-10" data-testid="subscriber-main">
          {loading && !dashboard ? (
            <div className="text-center font-serif italic text-[var(--bloom-ink-muted)] py-20">Loading your bloom club…</div>
          ) : !dashboard?.active_subscription ? (
            <EmptyState navigate={navigate} />
          ) : (
            <>
              {active === "dashboard" && (
                <DashboardPanel dashboard={dashboard} setActive={setActive} />
              )}
              {active === "subscription" && (
                <SubscriptionPanel dashboard={dashboard} reload={reload} />
              )}
              {active === "deliveries" && (
                <DeliveriesPanel dashboard={dashboard} />
              )}
              {active === "grower" && (
                <GrowerPanel dashboard={dashboard} />
              )}
              {active === "billing" && (
                <BillingPanel reload={reload} />
              )}
              {active === "account" && (
                <AccountPanel user={user} reload={reload} refreshAuth={refresh} />
              )}
            </>
          )}
        </main>
      </div>

      {showWelcome && (
        <div className="fixed top-24 right-6 z-50 bloom-card p-4 max-w-sm shadow-xl animate-in fade-in slide-in-from-right" data-testid="welcome-toast">
          <div className="flex items-start gap-3">
            <StatusIcon
              name="badge.founding_member"
              size={48}
              fallback={<Flower2 className="text-[var(--bloom-brand)] flex-shrink-0" strokeWidth={1.5} size={20} />}
            />
            <div>
              <div className="font-serif text-base">Welcome to the club!</div>
              <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)]">We're so glad you're here.</div>
            </div>
          </div>
        </div>
      )}

      <PublicFooter />
    </div>
  );
}

/* ---------------- Dashboard panel ---------------- */
function DashboardPanel({ dashboard, setActive }) {
  const sub = dashboard.active_subscription;
  const balance = dashboard.balance_due_total || 0;
  const tenure = dashboard.tenure;
  return (
    <div data-testid="subscriber-dashboard">
      <div className="mb-8">
        <div className="bloom-divider mb-4 text-left">✦ Welcome to The Club ✦</div>
        <h1 className="font-serif text-5xl md:text-6xl">Hello, <em>{dashboard.user?.name?.split(" ")[0]}</em>.</h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-3">
          Everything you need, in one quiet place.
        </p>
      </div>

      {/* Tenure badge */}
      {tenure && (
        <div className="bloom-card p-5 mb-6 flex items-center gap-5 flex-wrap" data-testid="tenure-card">
          <StatusIcon name={tenure.badge} size={72} fallback={null} className="flex-shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)]">Your status</div>
            <div className="font-serif text-2xl">{tenure.label}</div>
            <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-0.5">{tenure.tagline}</div>
            {tenure.next_tier && (
              <div className="font-serif italic text-xs text-[var(--bloom-ink-muted)] mt-2">
                {tenure.next_tier.seasons_until} more {tenure.next_tier.seasons_until === 1 ? "season" : "seasons"} to {tenure.next_tier.label}.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero card */}
      <div className="bloom-card p-6 md:p-8 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-5">
            <StatusIcon
              name={`subscription.${sub.plan_size}`}
              size={88}
              fallback={null}
              className="flex-shrink-0"
            />
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-1">Your subscription</div>
              <div className="font-serif text-3xl capitalize">{sub.plan_name}</div>
              <div className="font-serif italic text-[var(--bloom-ink-soft)] mt-1">
                {sub.duration_label} · {sub.delivery_count} {sub.delivery_count === 1 ? "delivery" : "deliveries"}
              </div>
              <div className="font-serif italic text-sm text-[var(--bloom-ink-muted)] mt-2">{sub.season_name}</div>
            </div>
          </div>
          <SubStatusPill status={sub.status} />
        </div>
        {balance > 0 && (
          <div className="mt-5 p-4 bg-[var(--bloom-surface)] border border-[var(--bloom-border)] flex items-center justify-between gap-3 flex-wrap" style={{ borderRadius: 6 }} data-testid="balance-due">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)]">Balance due</div>
              <div className="font-serif text-2xl">${balance.toFixed(2)}</div>
            </div>
            <button onClick={() => setActive("billing")} className="bloom-btn bloom-btn-primary" data-testid="pay-balance-link">
              Pay balance <CreditCard size={14} className="ml-2 inline" />
            </button>
          </div>
        )}
      </div>

      {/* Sticker grid (the 6 tiles) */}
      <h2 className="font-serif text-2xl mb-4">Browse</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {STICKER_SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className="bloom-card p-5 text-left transition hover:-translate-y-0.5 hover:shadow-lg"
              style={{ borderRadius: 8 }}
              data-testid={`sticker-${s.id}`}
            >
              <StatusIcon
                name={s.iconKey}
                size={56}
                className="mb-3"
                fallback={<Icon size={28} strokeWidth={1.2} className="text-[var(--bloom-accent)] mb-3" />}
              />
              <div className="font-serif text-xl">{s.label}</div>
              <div className="font-serif italic text-xs text-[var(--bloom-ink-muted)] mt-1">
                {s.id === "subscription" && "Plan, pause, cancel"}
                {s.id === "deliveries" && "Upcoming & past"}
                {s.id === "grower" && "Your assigned farmer"}
                {s.id === "billing" && "Payments & invoices"}
                {s.id === "account" && "Profile & password"}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Subscription panel ---------------- */
function SubscriptionPanel({ dashboard, reload }) {
  const sub = dashboard.active_subscription;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const act = async (action) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/me/subscriptions/${sub.id}/${action}`);
      await reload();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="subscriber-subscription-panel">
      <h1 className="font-serif text-4xl mb-2">My subscription</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">Everything about your bloom box.</p>

      <div className="bloom-card p-6 md:p-8 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="font-serif text-2xl capitalize">{sub.plan_name}</div>
            <div className="font-serif italic text-[var(--bloom-ink-soft)] text-sm mt-1">{sub.season_name}</div>
          </div>
          <SubStatusPill status={sub.status} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-[var(--bloom-border)] pt-5">
          <Stat label="Size" value={sub.plan_size} />
          <Stat label="Frequency" value={sub.plan_frequency} />
          <Stat label="Duration" value={sub.duration_label} />
          <Stat label="Deliveries" value={sub.delivery_count} />
          <Stat label="Start" value={sub.start_date} />
          <Stat label="End" value={sub.end_date} />
          <Stat label="Total value" value={`$${(sub.total_value || 0).toFixed(2)}`} />
          <Stat label="Balance due" value={`$${(sub.balance_due || 0).toFixed(2)}`} />
        </div>
        {sub.is_gift && (
          <div className="mt-5 pt-5 border-t border-[var(--bloom-border)]">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-2">Gift message</div>
            <div className="font-serif italic">{sub.gift_message || "·"}</div>
          </div>
        )}
      </div>

      <div className="bloom-card p-6">
        <h3 className="font-serif text-xl mb-2">Manage</h3>
        <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-4">
          Pause to skip an upcoming delivery. Cancel if your circumstances change, and we'll flag your subscription for refund review.
        </p>
        {err && <div className="mb-3 text-sm text-[var(--bloom-accent)] font-serif italic flex items-center gap-2"><AlertCircle size={14} /> {err}</div>}
        <div className="flex flex-wrap gap-3">
          {sub.status === "active" && (
            <button onClick={() => act("pause")} disabled={busy} className="bloom-btn bloom-btn-outline" data-testid="action-pause">
              <Pause size={14} className="mr-2 inline" /> Pause subscription
            </button>
          )}
          {sub.status === "paused" && (
            <button onClick={() => act("resume")} disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="action-resume">
              <Play size={14} className="mr-2 inline" /> Resume
            </button>
          )}
          {sub.status !== "cancelled" && sub.status !== "completed" && (
            <button onClick={() => {
              if (window.confirm("Are you sure you want to cancel? This will end your subscription and queue a refund review with the team.")) act("cancel");
            }} disabled={busy} className="bloom-btn bloom-btn-outline" data-testid="action-cancel">
              <X size={14} className="mr-2 inline" /> Cancel subscription
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Deliveries panel ---------------- */
function DeliveriesPanel({ dashboard }) {
  const sub = dashboard.active_subscription;
  // Compute a simple schedule preview locally from the duration / frequency
  const deliveries = useMemo(() => computeScheduledDeliveries(sub), [
    sub?.id, sub?.start_date, sub?.plan_frequency, sub?.delivery_count,
  ]);
  return (
    <div data-testid="subscriber-deliveries-panel">
      <h1 className="font-serif text-4xl mb-2">My deliveries</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
        Your upcoming bloom drop dates. Tracking links arrive in your inbox the day before each delivery.
      </p>

      <div className="bloom-card overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-[var(--bloom-border)]">
            <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Notes</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center font-serif italic text-[var(--bloom-ink-muted)]">No deliveries scheduled yet.</td></tr>
            )}
            {deliveries.map((d, i) => {
              const iconKey = i === 0 ? "delivery.scheduled" : "delivery.scheduled";
              return (
                <tr key={`${d.date}-${i}`} className="border-b border-[var(--bloom-border)]/40">
                  <td className="px-4 py-3 font-serif">{i + 1}</td>
                  <td className="px-4 py-3 font-serif">{d.date}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <StatusIcon name={iconKey} size={20} fallback={null} />
                      <span className="inline-flex px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase bg-[var(--bloom-surface)] text-[var(--bloom-ink-soft)]" style={{ borderRadius: 999 }}>
                        {d.status}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3 font-serif italic text-[var(--bloom-ink-soft)] text-sm">{d.notes}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)]">
        Dates are estimates and will be confirmed by your grower closer to delivery.
      </p>
    </div>
  );
}

const FREQUENCY_STEP_DAYS = { weekly: 7, fortnightly: 14, monthly: 30 };

function computeScheduledDeliveries(sub) {
  if (!sub || !sub.start_date) return [];
  const out = [];
  const start = new Date(sub.start_date);
  const stepDays = FREQUENCY_STEP_DAYS[sub.plan_frequency] || 30;
  const count = sub.delivery_count || 0;
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i * stepDays);
    out.push({
      date: d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short", year: "numeric" }),
      status: i === 0 ? "Upcoming" : "Scheduled",
      notes: i === 0 ? "First delivery" : "",
    });
  }
  return out;
}

/* ---------------- Grower panel ---------------- */
function GrowerPanel({ dashboard }) {
  const grower = dashboard.grower;
  const userRegion = grower?.region || dashboard.active_subscription?.delivery?.city || null;
  if (!grower) {
    return (
      <div data-testid="subscriber-grower-panel">
        <h1 className="font-serif text-4xl mb-2">My grower</h1>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
          You'll be matched with a Founding Grower closer to season start.
        </p>
        <NZMapCard userRegion={userRegion} />
        <div className="bloom-card p-10 text-center mt-6">
          <Sprout className="mx-auto text-[var(--bloom-accent)] mb-4" strokeWidth={1.2} size={42} />
          <h3 className="font-serif text-2xl mb-2">Matching soon</h3>
          <p className="font-serif italic text-[var(--bloom-ink-soft)] max-w-md mx-auto">
            We pair every subscriber with a local Founding Grower based on your region. You'll meet yours just before deliveries begin.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="subscriber-grower-panel">
      <h1 className="font-serif text-4xl mb-2">My grower</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">The hands behind your blooms.</p>
      <div className="bloom-card overflow-hidden">
        {grower.photo && (
          <div className="aspect-[16/9] bg-[var(--bloom-surface)] overflow-hidden">
            <img src={grower.photo} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-6 md:p-8">
          <div className="font-serif text-3xl">{grower.farm_name}</div>
          <div className="font-serif italic text-[var(--bloom-ink-soft)] mt-1">
            {grower.name} · {grower.town_city}{grower.region ? `, ${grower.region}` : ""}
          </div>
          {grower.vision && (
            <p className="font-serif italic mt-5 text-[var(--bloom-ink-soft)] leading-relaxed">"{grower.vision}"</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Billing panel ---------------- */
function BillingPanel({ reload }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      const [p, d] = await Promise.all([api.get("/me/payments"), api.get("/me/dashboard")]);
      setPayments(p.data.payments || []);
      setDashboard(d.data);
      setLoading(false);
    })();
  }, []);

  const payBalance = async () => {
    setBusy(true); setErr("");
    try {
      const sub = dashboard.active_subscription;
      const { data } = await api.post(`/me/subscriptions/${sub.id}/balance-checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (loading) return <div className="font-serif italic text-[var(--bloom-ink-muted)] py-20 text-center">Loading…</div>;
  const balance = dashboard?.balance_due_total || 0;

  return (
    <div data-testid="subscriber-billing-panel">
      <h1 className="font-serif text-4xl mb-2">Billing & payments</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">A neat ledger of everything you've paid.</p>

      {balance > 0 && (
        <div className="bloom-card p-6 mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)]">Balance owing</div>
            <div className="font-serif text-3xl">${balance.toFixed(2)}</div>
            <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-1">Pay it off any time, no rush.</div>
          </div>
          <button onClick={payBalance} disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="pay-balance-btn">
            {busy ? <Loader2 className="animate-spin inline mr-2" size={14} /> : <CreditCard size={14} className="mr-2 inline" />}
            {busy ? "Redirecting…" : "Pay balance with Stripe"}
          </button>
        </div>
      )}
      {err && <div className="mb-4 text-sm text-[var(--bloom-accent)] font-serif italic flex items-center gap-2"><AlertCircle size={14} /> {err}</div>}

      <div className="bloom-card overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-[var(--bloom-border)]">
            <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center font-serif italic text-[var(--bloom-ink-muted)]">No payments yet.</td></tr>
            )}
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-[var(--bloom-border)]/40" data-testid={`payment-${p.id}`}>
                <td className="px-4 py-3 font-serif text-sm">{new Date(p.created_at).toLocaleDateString("en-NZ")}</td>
                <td className="px-4 py-3 font-serif">{p.metadata?.plan_name || "Bloom box"}</td>
                <td className="px-4 py-3 font-serif italic text-sm capitalize text-[var(--bloom-ink-soft)]">{p.type}</td>
                <td className="px-4 py-3 font-serif">${(p.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase ${
                    p.status === "paid" ? "bg-[var(--bloom-ink)] text-[#FAF4FA]" : "bg-[var(--bloom-surface)] text-[var(--bloom-ink-soft)]"
                  }`} style={{ borderRadius: 999 }}>
                    {p.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Account panel ---------------- */
function AccountPanel({ user, reload, refreshAuth }) {
  const [form, setForm] = useState({
    name: user?.name || "",
    delivery_address: user?.delivery_address || "",
    delivery_suburb: user?.delivery_suburb || "",
    delivery_city: user?.delivery_city || "",
    delivery_postcode: user?.delivery_postcode || "",
    phone: user?.phone || "",
    marketing_opt_in: user?.marketing_opt_in ?? true,
  });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPw = (k, v) => setPwForm((f) => ({ ...f, [k]: v }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.patch("/me/profile", form);
      await refreshAuth();
      setMsg("Profile saved.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    if (pwForm.new_password !== pwForm.confirm) {
      setErr("New passwords don't match.");
      setBusy(false); return;
    }
    try {
      await api.post("/me/password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      setMsg("Password updated.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="subscriber-account-panel">
      <h1 className="font-serif text-4xl mb-2">My account</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">Keep your details fresh, blooms arrive sooner.</p>

      {msg && <div className="mb-4 p-3 border border-[var(--bloom-brand)] text-[var(--bloom-brand)] font-serif italic flex items-center gap-2"><Check size={14} /> {msg}</div>}
      {err && <div className="mb-4 p-3 border border-[var(--bloom-accent)] text-[var(--bloom-accent)] font-serif italic flex items-center gap-2"><AlertCircle size={14} /> {err}</div>}

      <form onSubmit={saveProfile} className="bloom-card p-6 md:p-8 mb-6" data-testid="profile-form">
        <h3 className="font-serif text-xl mb-5">Profile & delivery</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FieldBlock label="Name"><input className="bloom-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="account-name" /></FieldBlock>
          <FieldBlock label="Email (cannot be changed)"><input className="bloom-input" value={user?.email} disabled /></FieldBlock>
          <FieldBlock label="Phone"><input className="bloom-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="account-phone" /></FieldBlock>
          <FieldBlock label="Marketing emails">
            <label className="flex items-center gap-3 h-[42px]">
              <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => set("marketing_opt_in", e.target.checked)} data-testid="account-marketing" />
              <span className="font-serif text-sm">Send me grower stories & season updates</span>
            </label>
          </FieldBlock>
          <FieldBlock label="Delivery address"><input className="bloom-input" value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} data-testid="account-address" /></FieldBlock>
          <FieldBlock label="Suburb"><input className="bloom-input" value={form.delivery_suburb} onChange={(e) => set("delivery_suburb", e.target.value)} data-testid="account-suburb" /></FieldBlock>
          <FieldBlock label="City / town"><input className="bloom-input" value={form.delivery_city} onChange={(e) => set("delivery_city", e.target.value)} data-testid="account-city" /></FieldBlock>
          <FieldBlock label="Postcode"><input className="bloom-input" value={form.delivery_postcode} onChange={(e) => set("delivery_postcode", e.target.value)} data-testid="account-postcode" /></FieldBlock>
        </div>
        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="account-save">Save profile</button>
        </div>
      </form>

      <form onSubmit={savePassword} className="bloom-card p-6 md:p-8" data-testid="password-form">
        <h3 className="font-serif text-xl mb-5">Change password</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <FieldBlock label="Current password"><input type="password" className="bloom-input" value={pwForm.current_password} onChange={(e) => setPw("current_password", e.target.value)} data-testid="account-pw-current" /></FieldBlock>
          <FieldBlock label="New password"><input type="password" className="bloom-input" value={pwForm.new_password} onChange={(e) => setPw("new_password", e.target.value)} data-testid="account-pw-new" /></FieldBlock>
          <FieldBlock label="Confirm new"><input type="password" className="bloom-input" value={pwForm.confirm} onChange={(e) => setPw("confirm", e.target.value)} data-testid="account-pw-confirm" /></FieldBlock>
        </div>
        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={busy} className="bloom-btn bloom-btn-outline" data-testid="account-pw-save">Update password</button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Empty state ---------------- */
function EmptyState({ navigate }) {
  return (
    <div className="max-w-xl mx-auto text-center py-20" data-testid="subscriber-empty">
      <Flower2 className="mx-auto text-[var(--bloom-accent)] mb-4" strokeWidth={1.2} size={48} />
      <h1 className="font-serif text-4xl mb-3">No subscription yet</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">
        Ready to build your bloom box? It only takes a couple of minutes.
      </p>
      <button onClick={() => navigate("/subscribe")} className="bloom-btn bloom-btn-primary" data-testid="goto-subscribe">Start your subscription</button>
    </div>
  );
}

/* ---------------- Bits & pieces ---------------- */
function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-1">{label}</div>
      <div className="font-serif text-base capitalize">{value || "·"}</div>
    </div>
  );
}

function FieldBlock({ label, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}

const STATUS_COLOURS = {
  active: "bg-[var(--bloom-ink)] text-[#FAF4FA]",
  paused: "bg-[var(--bloom-warning)]/20 text-[var(--bloom-warning)]",
  cancelled: "bg-[var(--bloom-accent)]/15 text-[var(--bloom-accent)]",
};
const STATUS_COLOUR_FALLBACK = "bg-[var(--bloom-surface)] text-[var(--bloom-ink-soft)]";

function SubStatusPill({ status }) {
  const colour = STATUS_COLOURS[status] || STATUS_COLOUR_FALLBACK;
  return (
    <span className={`inline-flex px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase ${colour}`} style={{ borderRadius: 999 }}>
      {status}
    </span>
  );
}

function NZMapCard({ userRegion }) {
  return (
    <div className="bloom-card p-6 md:p-8" data-testid="subscriber-map-card">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-2">Our growing network</div>
      <NZTerritoryMap
        pins={userRegion ? [{ region: userRegion, count: 1 }] : []}
        activeRegion={userRegion}
        size="default"
        showLegend={false}
      />
    </div>
  );
}
